import React, { useState, useEffect, useRef } from 'react';
import { motion, useMotionValue, useTransform, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import './Hero.css';

const Hero: React.FC = () => {
  const [currentFeature, setCurrentFeature] = useState(0);
  const [isTreeInteractive, setIsTreeInteractive] = useState(false);
  const [showBurst, setShowBurst] = useState(false);
  const [leafCount, setLeafCount] = useState(30); // Increased leaf count
  const treeRef = useRef<HTMLDivElement>(null);
  
  const features = [
    "Study in Japan's Top Language School & Universities",
    "Professional Visa Guidance",
    "Scholarship Assistance",
    // "Cultural Adaptation Support"
  ];

  const x = useMotionValue(0);
  const rotate = useTransform(x, [-100, 100], [-10, 10]);
  const scale = useTransform(x, [-100, 100], [0.9, 1.05]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setCurrentFeature((prev) => (prev + 1) % features.length);
    }, 3000);
    return () => clearTimeout(timer);
  }, [currentFeature, features.length]);
  
  // Create a gust of wind effect to drop more leaves on interaction
  const handleTreeShake = () => {
    if (!isTreeInteractive) {
      setIsTreeInteractive(true);
      setShowBurst(true);
      setLeafCount(prev => prev + 15); // Add more leaves on shake
      
      // Apply random transformation to tree when shaking
      if (treeRef.current) {
        treeRef.current.style.transform = `perspective(1000px) rotateX(${Math.random() * 3}deg) rotateY(${Math.random() * 3}deg)`;
        
        // Reset transformation after shake
        setTimeout(() => {
          if (treeRef.current) {
            treeRef.current.style.transform = '';
          }
        }, 800);
      }
      
      // Reset burst effect
      setTimeout(() => {
        setShowBurst(false);
      }, 700);
      
      // Reset tree shake animation
      setTimeout(() => {
        setIsTreeInteractive(false);
      }, 1000);
    }
  };

  return (
    <div id="home" className="hero-section relative pt-20 flex items-center overflow-hidden">
      {/* Animated background gradient */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-50 to-slate-100 overflow-hidden">
        <motion.div 
          className="absolute -top-10 -right-10 w-96 h-96 bg-primary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, 30],
            y: [0, -30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 20,
            ease: "easeInOut" 
          }}
        />
        
        <motion.div 
          className="absolute top-1/3 left-1/4 w-96 h-96 bg-secondary/10 rounded-full blur-3xl"
          animate={{ 
            x: [0, -30],
            y: [0, 30]
          }}
          transition={{ 
            repeat: Infinity,
            repeatType: "reverse",
            duration: 15,
            ease: "easeInOut" 
          }}
        />
      </div>

      {/* Japanese character background */}
      <div className="absolute inset-0 overflow-hidden opacity-5 select-none pointer-events-none">
        <div className="japanese-pattern"></div>
      </div>
      
      <div className="container mx-auto px-4 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 items-center">
          {/* Left column: Content */}
          <motion.div 
            initial={{ opacity: 0, x: -50 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="order-2 lg:order-1"
          >
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3, duration: 0.8 }}
              className="mb-4 inline-block px-4 py-1 bg-primary/10 text-primary rounded-full text-sm font-medium"
            >
              Your Future in Japan Awaits
            </motion.div>
            
            <h1 className="mb-2 text-4xl md:text-5xl lg:text-6xl font-bold">
              <motion.span 
                className="block text-slate-800"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.8, delay: 0.4 }}
              >
                Your Journey to
              </motion.span>
              <motion.span 
                className="text-primary inline-block"
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.6 }}
              >
                Japan
              </motion.span>
              <motion.span 
                className="text-secondary inline-block"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8, delay: 0.8 }}
              >
                {" "}Starts Here!
              </motion.span>
            </h1>
            
            {/* Animated feature text */}
            <div className="h-8 mb-6">
              <AnimatePresence mode="wait">
                <motion.p 
                  key={currentFeature}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -20 }}
                  transition={{ duration: 0.5 }}
                  className="text-lg text-slate-600"
                >
                  ✨ {features[currentFeature]}
                </motion.p>
              </AnimatePresence>
            </div>
            
            <motion.p 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.8, delay: 1 }}
              className="mb-8 text-slate-600 max-w-xl"
            >
              Expert guidance for your study abroad journey to Japan. We handle everything from university selection to visa processing, ensuring a seamless experience with our team of dedicated consultants.
            </motion.p>
            
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 1.2 }}
              className="flex flex-wrap gap-4"
            >
              <Link 
                to="/services"
                className="btn-primary"
              >
                <span className="flex items-center gap-2">
                  Get Started <i className="fa-solid fa-arrow-right"></i>
                </span>
              </Link>
              
              <Link 
                to="/about"
                className="btn-secondary"
              >
                <span className="flex items-center gap-2">
                  Learn More
                </span>
              </Link>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 1.4 }}
              className="mt-12 glass p-6 max-w-md rounded-xl"
              whileHover={{ y: -5 }}
            >
              <div className="flex justify-around gap-6">
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">2000+</h4>
                  <p className="text-sm text-slate-600">Students Placed</p>
                </div>
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">90%</h4>
                  <p className="text-sm text-slate-600">Success Rate</p>
                </div>
                <div className="flex flex-col items-center">
                  <h4 className="text-2xl font-bold bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">30+</h4>
                  <p className="text-sm text-slate-600">University Partners</p>
                </div>
              </div>
            </motion.div>
          </motion.div>
          
          {/* Right column: Interactive Tree Animation */}
          <motion.div 
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1 }}
            className="order-1 lg:order-2 h-[400px] lg:h-[600px] flex items-center justify-center perspective"
            style={{ x, rotate, scale }}
            drag
            dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
            dragElastic={0.1}
            whileTap={{ scale: 1.02 }}
            onClick={handleTreeShake}
          >
            <div className="relative w-full max-w-md h-full" ref={treeRef}>
              {/* Animated Tree with SVG */}
              <div className={`tree-container ${isTreeInteractive ? 'tree-shake' : ''}`}>
                {/* Tree Shadow */}
                <motion.div 
                  className="absolute bottom-0 left-1/2 w-[80%] h-10 bg-black/10 rounded-[50%] blur-md z-0"
                  style={{ translateX: '-50%' }}
                  animate={{
                    width: isTreeInteractive ? '85%' : '80%',
                    opacity: isTreeInteractive ? 0.15 : 0.1
                  }}
                  transition={{ duration: 0.5 }}
                />
                
                {/* Main Tree SVG with animated branches */}
                <motion.div 
                  className="absolute bottom-0 left-1/2 w-[90%] h-[95%] z-10"
                  style={{ translateX: '-50%' }}
                  animate={{ 
                    y: [0, -3, 0, -2, 0],
                    rotate: [0, 0.3, 0, -0.3, 0]
                  }}
                  transition={{
                    duration: 10,
                    repeat: Infinity,
                    ease: "easeInOut"
                  }}
                >
                  <motion.img 
                    src="tree/tree.svg" 
                    alt="Cherry Blossom Tree" 
                    className="w-full h-full object-contain object-bottom"
                    animate={isTreeInteractive ? {
                      scale: [1, 1.02, 0.98, 1],
                      translateY: [0, -5, 5, 0],
                    } : {}}
                    transition={{ duration: 0.6 }}
                    style={{
                      filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.1))"
                    }}
                  />
                  
                  {/* Animated branches - left side */}
                  <motion.div 
                    className="absolute top-[18%] left-[30%] w-[35%] h-[30%] origin-bottom-left"
                    animate={{ 
                      rotate: [0, 1.2, 0.5, 1.5, 0],
                      x: [0, -2, 0, -1, 0],
                      y: [0, -1, 0, -2, 0]
                    }}
                    transition={{ 
                      duration: 8, 
                      repeat: Infinity,
                      ease: "easeInOut" 
                    }}
                  >
                    <motion.img 
                      src="tree/tree.svg" 
                      alt="" 
                      className="w-full h-full object-contain opacity-0"
                    />
                  </motion.div>
                  
                  {/* Animated branches - right side */}
                  <motion.div 
                    className="absolute top-[15%] right-[25%] w-[35%] h-[30%] origin-bottom-right"
                    animate={{ 
                      rotate: [0, -1.7, -0.5, -1.2, 0],
                      x: [0, 2, 0, 1, 0],
                      y: [0, -2, 0, -1, 0]
                    }}
                    transition={{ 
                      duration: 9, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 0.5
                    }}
                  >
                    <motion.img 
                      src="tree/tree.svg" 
                      alt="" 
                      className="w-full h-full object-contain opacity-0"
                    />
                  </motion.div>
                  
                  {/* Animated branches - top center */}
                  <motion.div 
                    className="absolute top-[5%] left-[42%] w-[30%] h-[25%] origin-bottom"
                    animate={{ 
                      rotate: [0, 0.8, 0, -0.8, 0],
                      y: [0, -3, -1, -2, 0]
                    }}
                    transition={{ 
                      duration: 7, 
                      repeat: Infinity,
                      ease: "easeInOut",
                      delay: 1
                    }}
                  >
                    <motion.img 
                      src="tree/tree.svg" 
                      alt="" 
                      className="w-full h-full object-contain opacity-0"
                    />
                  </motion.div>
                </motion.div>
                
                {/* Leaf burst effect when tree is shaken */}
                {showBurst && (
                  <>
                    <motion.div 
                      className="absolute top-1/3 left-1/2 w-48 h-48 rounded-full bg-gradient-radial from-pink-200/30 via-pink-100/15 to-transparent z-20"
                      initial={{ scale: 0, x: "-50%", y: "-50%" }}
                      animate={{ scale: [0, 1.5, 0], opacity: [0, 0.6, 0] }}
                      transition={{ duration: 0.8 }}
                    />
                    <motion.div 
                      className="absolute top-2/5 left-1/2 w-32 h-32 rounded-full bg-gradient-radial from-pink-300/20 via-pink-200/10 to-transparent z-20"
                      initial={{ scale: 0, x: "-60%", y: "-40%" }}
                      animate={{ scale: [0, 1.2, 0], opacity: [0, 0.7, 0] }}
                      transition={{ duration: 0.7, delay: 0.1 }}
                    />
                    {/* Burst of petals */}
                    {Array.from({ length: 12 }).map((_, i) => (
                      <motion.div
                        key={`burst-petal-${i}`}
                        className="absolute z-30 pointer-events-none"
                        style={{
                          top: "35%",
                          left: "50%",
                          width: `${5 + Math.random() * 7}px`,
                          height: `${5 + Math.random() * 7}px`,
                          translateX: '-50%',
                          translateY: '-50%'
                        }}
                        initial={{ 
                          opacity: 0,
                          scale: 0.2
                        }}
                        animate={{ 
                          opacity: [0, 1, 0.7, 0],
                          scale: [0.2, 1, 0.8, 0.5],
                          x: [0, (Math.random() * 100 - 50) * 1.5],
                          y: [0, (Math.random() * 100 - 50) * 1.5],
                          rotate: [0, Math.random() * 360]
                        }}
                        transition={{
                          duration: 0.8,
                          ease: "easeOut"
                        }}
                      >
                        <img 
                          src={`tree/l${1 + Math.floor(Math.random() * 20)}.svg`} 
                          alt="" 
                          className="w-full h-full"
                        />
                      </motion.div>
                    ))}
                  </>
                )}
                
                {/* Falling flowers */}
                {Array.from({ length: leafCount }).map((_, i) => (
                  <motion.div 
                    key={`flower-${i}`} 
                    className="absolute z-30 pointer-events-none"
                    style={{
                      top: `-${Math.random() * 10}%`,
                      left: `${10 + Math.random() * 80}%`,
                      width: `${8 + Math.random() * 12}px`,
                      height: `${8 + Math.random() * 12}px`,
                      filter: `drop-shadow(0 1px 2px rgba(0,0,0,0.1)) brightness(${0.9 + Math.random() * 0.2})`
                    }}
                    initial={{ 
                      opacity: 0,
                      y: 0,
                      rotate: Math.random() * 360,
                      scale: 0.5 + Math.random() * 0.5
                    }}
                    animate={{ 
                      opacity: [0, 1, 1, 0.8, 0.5, 0.3, 0],
                      y: [0, 150, 300, 400, 450, 470, 480],
                      x: [
                        0,
                        Math.random() * 40 - 20,
                        Math.random() * 80 - 40,
                        Math.random() * 100 - 50,
                        Math.random() * 120 - 60,
                        Math.random() * 130 - 65,
                        Math.random() * 140 - 70
                      ],
                      rotate: [
                        Math.random() * 360,
                        Math.random() * 360 + 90,
                        Math.random() * 360 + 180,
                        Math.random() * 360 + 270,
                        Math.random() * 360 + 360,
                        Math.random() * 360 + 400,
                        Math.random() * 360 + 420
                      ],
                      scale: [
                        0.7 + Math.random() * 0.3,
                        0.8 + Math.random() * 0.2,
                        0.7 + Math.random() * 0.3,
                        0.6 + Math.random() * 0.2,
                        0.5 + Math.random() * 0.2,
                        0.4 + Math.random() * 0.1,
                        0.3 + Math.random() * 0.1
                      ]
                    }}
                    transition={{
                      duration: 8 + Math.random() * 7,
                      repeat: Infinity,
                      delay: Math.random() * 20,
                      ease: "easeInOut",
                      times: [0, 0.15, 0.3, 0.5, 0.7, 0.85, 1]
                    }}
                  >
                    <img 
                      src={`tree/l${1 + Math.floor(Math.random() * 20)}.svg`} 
                      alt="Cherry blossom" 
                      className="w-full h-full"
                      style={{ 
                        transform: `rotateY(${Math.random() * 180}deg)`,
                        mixBlendMode: Math.random() > 0.5 ? 'normal' : 'multiply'
                      }}
                    />
                  </motion.div>
                ))}
                
                {/* Ground Flowers - static flowers that appear to have fallen */}
                {Array.from({ length: 20 }).map((_, i) => (
                  <motion.div 
                    key={`ground-flower-${i}`}
                    className="absolute bottom-0 z-5 pointer-events-none"
                    style={{
                      left: `${Math.random() * 100}%`,
                      width: `${6 + Math.random() * 10}px`,
                      height: `${6 + Math.random() * 10}px`,
                      rotate: `${Math.random() * 360}deg`,
                      opacity: 0.7 + Math.random() * 0.3,
                      translateY: `${-Math.random() * 5}px`,
                      filter: `drop-shadow(0 1px 1px rgba(0,0,0,0.1)) brightness(${0.85 + Math.random() * 0.3})`
                    }}
                    animate={{
                      y: [0, -1, -2, -1, 0],
                      rotate: [`${Math.random() * 360}deg`, `${Math.random() * 360 + 5}deg`, `${Math.random() * 360 + 10}deg`, `${Math.random() * 360 + 5}deg`, `${Math.random() * 360}deg`],
                      scale: [1, 1.01, 1.02, 1.01, 1]
                    }}
                    transition={{
                      duration: 4 + Math.random() * 3,
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut",
                      delay: Math.random() * 2
                    }}
                  >
                    <img 
                      src={`tree/l${1 + Math.floor(Math.random() * 20)}.svg`} 
                      alt="Cherry blossom on ground" 
                      className="w-full h-full"
                      style={{ 
                        transform: `rotateY(${Math.random() * 180}deg) rotateX(${60 + Math.random() * 20}deg)`,
                        transformOrigin: 'center'
                      }}
                    />
                  </motion.div>
                ))}
                
                {/* Additional scattered small petals for realism */}
                {Array.from({ length: 15 }).map((_, i) => (
                  <motion.div 
                    key={`petal-${i}`}
                    className="absolute bottom-2 z-5 pointer-events-none"
                    style={{
                      left: `${Math.random() * 100}%`,
                      width: `${3 + Math.random() * 5}px`,
                      height: `${3 + Math.random() * 5}px`,
                      rotate: `${Math.random() * 360}deg`,
                      opacity: 0.6 + Math.random() * 0.2,
                      translateY: `${-Math.random() * 7}px`,
                      filter: 'blur(0.5px)'
                    }}
                    animate={{
                      x: [0, Math.random() * 8 - 4, 0],
                      y: [0, Math.random() * -3, 0],
                      rotate: [`${Math.random() * 360}deg`, `${Math.random() * 360 + 20}deg`, `${Math.random() * 360}deg`]
                    }}
                    transition={{
                      duration: 2 + Math.random() * 2,
                      repeat: Infinity,
                      repeatType: "reverse",
                      ease: "easeInOut",
                      delay: Math.random() * 1
                    }}
                  >
                    <img 
                      src={`tree/l${1 + Math.floor(Math.random() * 20)}.svg`} 
                      alt="" 
                      className="w-full h-full"
                    />
                  </motion.div>
                ))}
              </div>
              
              {/* Interactive instruction overlay */}
              <motion.div 
                className="absolute bottom-6 right-4 bg-white/40 backdrop-blur-md px-4 py-2 rounded-full text-xs font-medium border border-white/50 shadow-md flex items-center gap-2"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 2 }}
                whileHover={{ scale: 1.05, backgroundColor: "rgba(255,255,255,0.6)" }}
              >
                <svg className="w-4 h-4 text-slate-700 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 15l-2 5L9 9l11 4-5 2zm0 0l5 5M7.188 2.239l.777 2.897M5.136 7.965l-2.898-.777M13.95 4.05l-2.122 2.122m-5.657 5.656l-2.12 2.122" />
                </svg>
                <span className="text-slate-800">Drag or Tap the Tree</span>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default Hero; 